import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Label } from './ui/label';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import CustomerLocationMap from './CustomerLocationMap';
import { toast } from 'sonner';
import { Camera, ImageIcon, Loader2, CheckCircle2, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Delta #78: status ketersediaan username PPPoE/Hotspot
const UsernameStatus = ({ check, onPick }) => {
  if (!check) return null;
  if (check.loading) {
    return (
      <p className="text-xs text-slate-500 flex items-center gap-1" data-testid="pppoe-username-checking">
        <Loader2 className="w-3 h-3 animate-spin" /> Mengecek ketersediaan...
      </p>
    );
  }
  if (check.available) {
    return (
      <p className="text-xs font-semibold text-green-700 dark:text-green-400 flex items-center gap-1" data-testid="pppoe-username-available">
        <CheckCircle2 className="w-3.5 h-3.5" /> Username tersedia
      </p>
    );
  }
  if (check.available !== false) return null;
  const c = check.customer;
  let who = 'akun RADIUS lain';
  if (check.reason === 'voucher') who = 'voucher hotspot';
  if (check.reason === 'own_tenant' && c) {
    who = `${c.name} (${c.account_number})${c.branch_name ? ' · ' + c.branch_name : ''}${c.in_trash ? ' · di Arsip Terhapus' : ''}`;
  }
  if (check.reason === 'other_tenant') {
    who = check.tenant_name ? `tenant ${check.tenant_name}${c ? ' — ' + c.name + ' (' + c.account_number + ')' : ''}` : 'pelanggan di tenant lain';
  }
  return (
    <div className="space-y-1" data-testid="pppoe-username-taken">
      <p className="text-xs font-semibold text-red-600 dark:text-red-400 flex items-start gap-1">
        <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>Sudah dipakai: {who}</span>
      </p>
      {check.suggestions?.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-xs text-slate-500 dark:text-slate-400">Saran:</span>
          {check.suggestions.map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => onPick(s)}
              className="px-2 py-0.5 text-xs rounded-full border border-green-500 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors"
              data-testid={`pppoe-username-suggestion-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CustomerFormTabs = ({ 
  formData, 
  setFormData, 
  isEditMode = false,
  servicePlans = [],  // Receive from parent - already fetched in Customers.jsx
  branches = [],      // Receive from parent - already fetched in Customers.jsx
  owners = [],        // Receive from parent - already fetched in Customers.jsx
  isOwnerRole = false, // Is current user an owner?
  currentUser = null,  // Current user data for auto-filling owner
  connectionTypeFilter = null  // Filter from parent (pppoe/hotspot)
}) => {
  const [manualBranchSelection, setManualBranchSelection] = useState(false); // Track if user manually selected branch
  const [suggestedBranch, setSuggestedBranch] = useState(null); // Track auto-suggested branch

  // ODP/POP linking state
  const [odpList, setOdpList] = useState([]);
  const [nearestOdps, setNearestOdps] = useState([]);
  const [loadingNearest, setLoadingNearest] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [initialOdpLoaded, setInitialOdpLoaded] = useState(false);
  // FIX 2026-10-06 (Delta #40): default Tanggal Billing mengikuti tanggal instalasi
  // (permintaan user: buat tgl 6 -> perpanjang +30 hari -> tgl 6). Sebelumnya display
  // menampilkan "Tanggal 1" tapi submit mengirim due_date=10 (default tak konsisten).
  const _defaultBillingDay = (() => {
    try {
      const _iso = String(formData.installation_date || new Date().toISOString().slice(0, 10));
      const _d = parseInt(_iso.slice(8, 10), 10);
      if (!_d || _d < 1) return 1;
      return Math.min(_d, 28);
    } catch (_e) { return 1; }
  })();

  // Fetch tenant's active ODPs once
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API_URL}/api/odp-pop?per_page=500`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      const items = (res.data?.data || []).filter(o => o.status === 'active' || o.status === 'full');
      setOdpList(items);
    }).catch(() => setOdpList([]));
  }, []);

  // 2026-07-05: device photo upload state
  const [devicePhotoUploading, setDevicePhotoUploading] = useState(false);
  const cameraInputRef = React.useRef(null);
  const galleryInputRef = React.useRef(null);

  const handleDevicePhotoUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5 MB');
      return;
    }
    setDevicePhotoUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API_URL}/api/v2/customers/upload-device-photo`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      if (res.data?.success && res.data?.url) {
        setFormData((prev) => ({ ...prev, device_photo_url: res.data.url }));
        toast.success('Foto perangkat berhasil diunggah');
      } else {
        toast.error('Upload gagal');
      }
    } catch (err) {
      toast.error('Upload error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDevicePhotoUploading(false);
    }
  };

  // Pre-fill current ODP link when editing
  useEffect(() => {
    if (!isEditMode || initialOdpLoaded) return;
    const customerId = formData.id || formData.customer_id;
    if (!customerId) return;
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/odp-pop/by-customer/${customerId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      const link = res.data?.data;
      if (link?.odp_id) {
        setFormData(prev => ({ ...prev, odp_id: link.odp_id, odp_port_number: link.port_number }));
      }
      setInitialOdpLoaded(true);
    }).catch(() => setInitialOdpLoaded(true));
  }, [isEditMode, formData.id, formData.customer_id, initialOdpLoaded, setFormData]);

  const fetchNearestOdp = useCallback(async () => {
    const lat = parseFloat(formData.latitude);
    const lng = parseFloat(formData.longitude);
    if (isNaN(lat) || isNaN(lng)) return;
    setLoadingNearest(true);
    setShowSuggestions(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_URL}/api/odp-pop/nearest?lat=${lat}&lng=${lng}&limit=5&radius_km=5`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNearestOdps(res.data?.data || []);
    } catch (e) {
      setNearestOdps([]);
    } finally {
      setLoadingNearest(false);
    }
  }, [formData.latitude, formData.longitude]);

  const hasValidCoords = !isNaN(parseFloat(formData.latitude)) && !isNaN(parseFloat(formData.longitude));
  const selectedOdp = odpList.find(o => String(o.id) === String(formData.odp_id));

  // Auto-set connection_type based on filter from parent (for new customers only)
  useEffect(() => {
    if (!isEditMode && connectionTypeFilter && !formData.connection_type_set_by_filter) {
      // 2026-05-20 fix: URL filter 'dhcp' adalah alias lama untuk Static IP (legacy naming).
      // Tanpa baris ini, useEffect override connection_type ke 'PPPoE' walau menu Statik dipilih.
      const cf = connectionTypeFilter.toLowerCase();
      const connType = cf === 'hotspot' ? 'Hotspot' : 
                       cf === 'pppoe' ? 'PPPoE' : 
                       (cf === 'static' || cf === 'dhcp') ? 'Static' : 'PPPoE';
      setFormData(prev => ({ 
        ...prev, 
        connection_type: connType,
        connection_type_set_by_filter: true,
        service_plan: '' // Reset service plan when connection type changes
      }));
    }
  }, [connectionTypeFilter, isEditMode, formData.connection_type_set_by_filter, setFormData]);

  // Delta #78: cek username PPPoE/Hotspot 0,5 dtk setelah berhenti mengetik
  const [unameCheck, setUnameCheck] = useState(null);
  useEffect(() => {
    const u = String(formData.mikrotik_user || '').trim();
    const isStatic = String(formData.connection_type || '').toLowerCase() === 'static';
    if (!u || isStatic) {
      setUnameCheck(null);
      setFormData((f) => (f._username_taken ? { ...f, _username_taken: false } : f));
      return undefined;
    }
    let alive = true;
    setUnameCheck({ loading: true });
    const t = setTimeout(async () => {
      try {
        const params = { username: u };
        const exId = isEditMode ? (formData.id || formData.customer_id) : null;
        if (exId) params.exclude_id = exId;
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v2/pppoe-username/check`, {
          params,
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!alive) return;
        const d = r.data?.data || null;
        setUnameCheck(d);
        setFormData((f) => ({ ...f, _username_taken: d ? d.available === false : false }));
      } catch (_e) {
        if (!alive) return;
        setUnameCheck(null);
        setFormData((f) => ({ ...f, _username_taken: false }));
      }
    }, 500);
    return () => { alive = false; clearTimeout(t); };
  }, [formData.mikrotik_user, formData.connection_type, formData.id, formData.customer_id, isEditMode, setFormData]);

  // Auto-set owner and branch if user is an owner role
  useEffect(() => {
    if (isOwnerRole && currentUser && !formData.owner) {
      // Auto-fill owner with current user's name
      const ownerName = currentUser.full_name || currentUser.username;
      
      // Use branch_id from currentUser if available
      const updateData = { owner: ownerName };
      if (currentUser.branch_id) {
        updateData.branch_id = currentUser.branch_id;
      }
      
      setFormData(prev => ({ ...prev, ...updateData }));
    }
  }, [isOwnerRole, currentUser, formData.owner, setFormData]);

  // Auto-suggest branch based on address
  const matchBranchByAddress = useCallback((address) => {
    if (!address || address.trim().length < 3 || branches.length === 0) {
      return null;
    }

    const addressLower = address.toLowerCase().trim();
    
    // Score-based matching for better accuracy
    let bestMatch = null;
    let highestScore = 0;
    
    branches.forEach(branch => {
      let score = 0;
      const branchName = branch.name.toLowerCase();
      const branchAddress = (branch.address || '').toLowerCase();
      
      // Priority 1: Exact branch name match (highest score)
      if (addressLower.includes(branchName)) {
        score += 100;
      }
      
      // Priority 2: Match keywords from branch address
      const keywords = branchAddress.split(/[\s,]+/).filter(word => word.length > 3);
      keywords.forEach(keyword => {
        if (addressLower.includes(keyword)) {
          score += 10; // 10 points per keyword match
        }
      });
      
      // Update best match if this branch has higher score
      if (score > highestScore) {
        highestScore = score;
        bestMatch = branch;
      }
    });
    
    // Only return match if score is meaningful (at least 10 points)
    return highestScore >= 10 ? bestMatch : null;
  }, [branches]);

  // Auto-suggest branch when address changes
  useEffect(() => {
    // Only auto-suggest if:
    // 1. Not in edit mode (only for new customers)
    // 2. User hasn't manually selected a branch
    // 3. Address is provided
    // 4. Branches are loaded
    if (!isEditMode && !manualBranchSelection && formData.address && branches.length > 0) {
      const matched = matchBranchByAddress(formData.address);
      
      if (matched && matched.id !== formData.branch_id) {
        setSuggestedBranch(matched);
        setFormData(prev => ({ ...prev, branch_id: matched.id }));
      } else if (!matched) {
        setSuggestedBranch(null);
      }
    }
  }, [formData.address, formData.branch_id, branches, isEditMode, manualBranchSelection, matchBranchByAddress, setFormData]);

  return (
    <div className="space-y-4">
      {/* Info Notice */}
      {!isEditMode ? (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            💡 <strong>ID Pelanggan</strong> akan di-generate otomatis dengan format <strong>yymmdd-xxxxx</strong> (contoh: 250115-00001)
          </p>
        </div>
      ) : formData.account_number && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-sm text-green-700 dark:text-green-300">
            📋 <strong>ID Pelanggan:</strong> <span className="font-mono bg-green-100 dark:bg-green-800 px-2 py-0.5 rounded">{formData.account_number}</span>
          </p>
        </div>
      )}

      <Tabs defaultValue="info-pelanggan" className="w-full">
        <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full h-auto gap-1 p-1 sm:gap-0 sm:p-1">
          <TabsTrigger value="info-pelanggan" className="flex-1 min-w-[100px] text-xs sm:text-sm py-2 sm:py-1.5">Info Pelanggan</TabsTrigger>
          <TabsTrigger value="paket-langganan" className="flex-1 min-w-[100px] text-xs sm:text-sm py-2 sm:py-1.5">Paket Langganan</TabsTrigger>
          <TabsTrigger value="info-billing" className="flex-1 min-w-[100px] text-xs sm:text-sm py-2 sm:py-1.5">Info Billing</TabsTrigger>
          <TabsTrigger value="perangkat" className="flex-1 min-w-[100px] text-xs sm:text-sm py-2 sm:py-1.5" data-testid="tab-perangkat-trigger">Perangkat</TabsTrigger>
        </TabsList>

        {/* TAB 1: INFO PELANGGAN */}
        <TabsContent value="info-pelanggan" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nama Lengkap *</Label>
              <Input
                required
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                required
                placeholder="john@example.com"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>No. Telepon *</Label>
              <div className="flex items-center">
                <span className="px-3 py-2 bg-slate-100 border border-r-0 border-slate-200 rounded-l-lg text-sm text-slate-600 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300">
                  +62
                </span>
                <Input
                  required
                  placeholder="8123456789"
                  value={formData.phone?.replace(/^(\+?62|0)/, '') || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // Only digits
                    setFormData({...formData, phone: value ? `62${value}` : ''});
                  }}
                  className="rounded-l-none"
                />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">Contoh: 8123456789 (tanpa 0 di depan)</p>
            </div>
            <div className="space-y-2">
              <Label>Alamat *</Label>
              <Input
                required
                placeholder="Alamat lengkap"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>NIK (No. Identitas) *</Label>
              <Input
                required
                type="text"
                maxLength={16}
                placeholder="1234567890123456"
                value={formData.nik || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ''); // Only digits
                  setFormData({...formData, nik: value});
                }}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">Harus 16 digit angka</p>
            </div>
            <div className="space-y-2">
              <Label>Koordinat Lokasi (Opsional)</Label>
              <Input
                data-testid="customer-coords-input"
                type="text"
                placeholder="-6.400359, 106.761524 (paste dari Google Maps)"
                value={(formData.latitude || formData.longitude)
                  ? `${formData.latitude || ''}${formData.longitude ? ', ' + formData.longitude : ''}`
                  : ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  // Split by comma, semicolon, or whitespace -- tolerant to copy-paste from Google Maps
                  const parts = raw.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
                  setFormData({
                    ...formData,
                    latitude: parts[0] || '',
                    longitude: parts[1] || '',
                  });
                }}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Format: <span className="font-mono">latitude, longitude</span> &mdash; cukup paste hasil copy dari Google Maps
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Password Portal Pelanggan</Label>
              <Input
                type="text"
                placeholder="12345678"
                value={formData.password || '12345678'}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">Password untuk login ke portal pelanggan (default: 12345678)</p>
            </div>
          </div>

          <CustomerLocationMap formData={formData} setFormData={setFormData} />

          {/* ODP/POP Selector */}
          <div className="space-y-3 p-4 rounded-lg border-2 border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900" data-testid="odp-selector-section">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a6 6 0 00-6 6c0 4.5 6 10 6 10s6-5.5 6-10a6 6 0 00-6-6zm0 8a2 2 0 110-4 2 2 0 010 4z"/></svg>
                ODP/POP (Opsional)
              </Label>
              <button
                type="button"
                data-testid="odp-suggest-btn"
                disabled={!hasValidCoords || loadingNearest}
                onClick={fetchNearestOdp}
                className={`text-xs font-medium px-3 py-1.5 rounded-full transition-all flex items-center gap-1.5 ${
                  hasValidCoords
                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
                title={hasValidCoords ? 'Cari ODP terdekat dari koordinat pelanggan' : 'Isi Latitude dan Longitude terlebih dahulu'}
              >
                {loadingNearest ? (
                  <><svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"/><path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" className="opacity-75"/></svg> Mencari...</>
                ) : (
                  <>🎯 Saran ODP Terdekat</>
                )}
              </button>
            </div>

            <select
              data-testid="odp-select"
              name="odp_id"
              className="w-full p-2.5 border-2 border-slate-300 rounded-lg bg-white cursor-pointer hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all dark:border-slate-600 dark:bg-slate-800"
              value={formData.odp_id || ''}
              onChange={(e) => {
                const val = e.target.value;
                setFormData({ ...formData, odp_id: val ? parseInt(val) : null });
                setShowSuggestions(false);
              }}
            >
              <option value="">— Tidak terhubung ke ODP —</option>
              {odpList.map(o => {
                const cap = o.capacity || 0;
                const used = o.used_ports || 0;
                const avail = cap > 0 ? Math.max(0, cap - used) : null;
                const isFull = cap > 0 && used >= cap;
                return (
                  <option key={o.id} value={o.id} disabled={isFull && String(o.id) !== String(formData.odp_id)}>
                    {o.name}{o.branch_name ? ` — ${o.branch_name}` : ''}
                    {avail !== null ? ` (sisa ${avail}/${cap} port)` : ''}
                    {isFull ? ' [PENUH]' : ''}
                  </option>
                );
              })}
            </select>

            {selectedOdp && (
              <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2 dark:text-green-300 dark:bg-green-900/30">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                Terhubung ke <strong>{selectedOdp.name}</strong>
                {formData.odp_port_number ? ` — Port ${formData.odp_port_number}` : ' (port akan di-assign otomatis)'}
              </div>
            )}

            {showSuggestions && (
              <div className="space-y-2" data-testid="odp-suggestions">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-slate-600 dark:text-slate-300">
                    {nearestOdps.length > 0
                      ? `${nearestOdps.length} ODP terdekat (radius 5 km):`
                      : 'Tidak ada ODP aktif dalam radius 5 km.'}
                  </p>
                  <button type="button" onClick={() => setShowSuggestions(false)} className="text-xs text-slate-400 hover:text-slate-600 dark:text-slate-500">Tutup</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {nearestOdps.map(o => {
                    const isSelected = String(o.id) === String(formData.odp_id);
                    return (
                      <button
                        key={o.id}
                        type="button"
                        data-testid={`odp-suggestion-${o.id}`}
                        onClick={() => {
                          setFormData({ ...formData, odp_id: o.id });
                          setShowSuggestions(false);
                        }}
                        className={`text-left p-3 rounded-lg border-2 transition-all ${
                          isSelected
                            ? 'border-green-500 bg-green-50'
                            : 'border-slate-200 bg-white hover:border-blue-400 hover:shadow-sm'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-800 truncate dark:text-slate-200">{o.name}</p>
                            <p className="text-xs text-slate-500 truncate dark:text-slate-400">{o.branch_name || '-'}</p>
                          </div>
                          <span className="text-[11px] font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full whitespace-nowrap dark:text-blue-400 dark:bg-blue-900/40">
                            {o.distance_km} km
                          </span>
                        </div>
                        <div className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-600 dark:text-slate-300">
                          <span>Sisa: <strong>{o.available_ports ?? '-'}/{o.capacity}</strong> port</span>
                          {isSelected && <span className="text-green-600 font-semibold dark:text-green-400">✓ Dipilih</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Pilih ODP secara manual, atau klik tombol di atas untuk mendapat saran otomatis berdasarkan lokasi pelanggan.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Cabang *</Label>
                {suggestedBranch && !manualBranchSelection && (
                  <span className="text-xs text-green-600 font-medium flex items-center gap-1 dark:text-green-400">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Terdeteksi otomatis
                  </span>
                )}
              </div>
              {isOwnerRole ? (
                // Owner role: Show branch name, disabled (auto-filled from owner data)
                <>
                  <input
                    type="text"
                    className="w-full p-2.5 border-2 border-slate-300 rounded-lg bg-slate-100 cursor-not-allowed dark:border-slate-600 dark:bg-slate-800"
                    value={branches.find(b => String(b.id) === String(formData.branch_id))?.name || 'Loading...'}
                    disabled
                    readOnly
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    📍 Cabang otomatis sesuai data owner Anda
                  </p>
                </>
              ) : (
                // Admin/SuperAdmin: Show full dropdown
                <>
                  <select
                    required
                    name="branch_id"
                    id="branch_id"
                    className={`w-full p-2.5 border-2 rounded-lg bg-white dark:bg-slate-800 cursor-pointer transition-all appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%278%27 viewBox=%270 0 12 8%27%3e%3cpath fill=%27%23334155%27 d=%27M6 8L0 0h12z%27/%3e%3c/svg%3e')] bg-[length:12px] bg-[center_right_0.75rem] bg-no-repeat pr-10 ${
                      suggestedBranch && !manualBranchSelection 
                        ? 'border-green-400 bg-green-50 dark:bg-green-900/30 dark:border-green-500 hover:border-green-500 focus:border-green-500 focus:ring-green-200' 
                        : 'border-slate-300 dark:border-slate-600 hover:border-blue-400 focus:border-blue-500 focus:ring-blue-200'
                    } focus:ring-2`}
                    value={formData.branch_id || ''}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      setManualBranchSelection(true); // Mark as manually selected
                      setSuggestedBranch(null); // Clear suggestion indicator
                      setFormData({...formData, branch_id: selectedValue});
                    }}
                  >
                    <option value="">Pilih Cabang</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    💡 Cabang akan terdeteksi otomatis dari alamat, Anda bisa mengubahnya manual
                  </p>
                </>
              )}
            </div>
            <div className="space-y-2">
              <Label>Pemilik/Owner *</Label>
              {isOwnerRole ? (
                // Owner role: Show only their own name, disabled
                <input
                  type="text"
                  className="w-full p-2.5 border-2 border-slate-300 rounded-lg bg-slate-100 cursor-not-allowed dark:border-slate-600 dark:bg-slate-800"
                  value={formData.owner || currentUser?.full_name || currentUser?.username || ''}
                  disabled
                  readOnly
                />
              ) : (
                // Admin/SuperAdmin: Show full dropdown
                <select
                  required
                  name="owner"
                  className="w-full p-2.5 border-2 border-slate-300 rounded-lg bg-white cursor-pointer hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%278%27 viewBox=%270 0 12 8%27%3e%3cpath fill=%27%23334155%27 d=%27M6 8L0 0h12z%27/%3e%3c/svg%3e')] bg-[length:12px] bg-[center_right_0.75rem] bg-no-repeat pr-10 dark:border-slate-600 dark:bg-slate-800"
                  value={formData.owner || ''}
                  onChange={(e) => {
                    const selectedOwner = owners.find(o => o.name === e.target.value);
                    const updates = { ...formData, owner: e.target.value };
                    if (selectedOwner && selectedOwner.branch_id) {
                      updates.branch_id = String(selectedOwner.branch_id);
                      setManualBranchSelection(false);
                      setSuggestedBranch(null);
                    }
                    setFormData(updates);
                  }}
                >
                  <option value="">Pilih Owner</option>
                  {owners.map((owner) => (
                    <option key={owner.id} value={owner.name}>
                      {owner.name}{owner.branch_id ? '' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>IP Address</Label>
              <Input
                disabled
                value={formData.ip_address}
                className="bg-gray-100 dark:bg-gray-800"
              />
              <p className="text-xs text-gray-500 dark:text-slate-400">Generate otomatis</p>
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Input
                placeholder="Contoh: ODP"
                value={formData.note || ''}
                onChange={(e) => setFormData({...formData, note: e.target.value})}
              />
              <p className="text-xs text-gray-500 dark:text-slate-400">Catatan tambahan</p>
            </div>
          </div>
        </TabsContent>

        {/* TAB 2: PAKET LANGGANAN */}
        <TabsContent value="paket-langganan" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Paket Langganan *</Label>
              <Select
                value={formData.service_plan}
                onValueChange={(value) => {
                  const selectedPlan = servicePlans.find(p => p.name === value);
                  setFormData({
                    ...formData, 
                    service_plan: value,
                    monthly_fee: selectedPlan ? selectedPlan.price : formData.monthly_fee
                  });
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Pilih paket" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {servicePlans
                    .filter(plan => {
                      const connType = formData.connection_type?.toLowerCase() || 'pppoe';
                      const planType = plan.package_type?.toLowerCase() || 'pppoe';
                      
                      if (planType === 'all') {
                        return true;
                      }
                      
                      if (connType === 'pppoe' || connType === 'static') {
                        return planType === 'pppoe';
                      } else if (connType === 'hotspot') {
                        return planType === 'hotspot';
                      }
                      return true;
                    })
                    .map(plan => (
                      <SelectItem key={plan.id} value={plan.name}>
                        {plan.name} - {plan.download_speed}/{plan.upload_speed} Mbps - Rp {plan.price?.toLocaleString('id-ID')}
                        {plan.branch_name && plan.branch_id !== 'all' && ` (${plan.branch_name})`}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {servicePlans.filter(plan => {
                const connType = formData.connection_type?.toLowerCase() || 'pppoe';
                const planType = plan.package_type?.toLowerCase() || 'pppoe';
                // Show packages with type 'all' for any connection type
                if (planType === 'all') return true;
                if (connType === 'pppoe' || connType === 'static') return planType === 'pppoe';
                if (connType === 'hotspot') return planType === 'hotspot';
                return true;
              }).length === 0 && (
                <p className="text-xs text-yellow-600 dark:text-yellow-400">⚠️ Belum ada paket {formData.connection_type}. Tambahkan di menu "Paket Layanan"</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Tipe Koneksi *</Label>
              {(() => {
                // Lock dropdown when navigated from a specific connection type page
                const path = typeof window !== 'undefined' ? window.location.pathname : '';
                const isOnStaticPage = path.includes('/customers/dhcp');
                const isOnPppoePage = path.includes('/customers/pppoe');
                const isOnHotspotPage = path.includes('/customers/hotspot');
                const locked = isOnStaticPage || isOnPppoePage || isOnHotspotPage;
                if (locked) {
                  const label = isOnStaticPage ? 'Static IP' : (isOnHotspotPage ? 'Hotspot' : 'PPPoE');
                  const badge = isOnStaticPage
                    ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-500/40 text-blue-800 dark:text-blue-300'
                    : (isOnHotspotPage ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-500/40 text-amber-800 dark:text-amber-300' : 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-500/40 text-green-800 dark:text-green-300');
                  return (
                    <div data-testid="conn-type-locked" className={`p-2.5 border-2 rounded-lg font-semibold flex items-center justify-between ${badge}`}>
                      <span>{label}</span>
                      <span className="text-[10px] font-normal opacity-70">🔒 Terkunci dari menu</span>
                    </div>
                  );
                }
                return (
                  <select
                    required
                    className="w-full p-2.5 border-2 border-slate-300 rounded-lg bg-white cursor-pointer hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%278%27 viewBox=%270 0 12 8%27%3e%3cpath fill=%27%23334155%27 d=%27M6 8L0 0h12z%27/%3e%3c/svg%3e')] bg-[length:12px] bg-[center_right_0.75rem] bg-no-repeat pr-10 dark:border-slate-600 dark:bg-slate-800"
                    value={formData.connection_type}
                    onChange={(e) => setFormData({...formData, connection_type: e.target.value, service_plan: ''})}
                  >
                    <option value="PPPoE">PPPoE</option>
                    <option value="Hotspot">Hotspot</option>
                    <option value="Static">Static IP</option>
                  </select>
                );
              })()}
              {(() => {
                const path = typeof window !== 'undefined' ? window.location.pathname : '';
                const isLocked = path.includes('/customers/dhcp') || path.includes('/customers/pppoe') || path.includes('/customers/hotspot');
                if (isLocked) {
                  return <p className="text-xs text-slate-500 dark:text-slate-400">Tipe koneksi mengikuti menu yang dipilih.</p>;
                }
                return <p className="text-xs text-slate-500 dark:text-slate-400">💡 Default: PPPoE. Mengubah tipe koneksi akan mereset pilihan paket.</p>;
              })()}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Status Registrasi</Label>
            <div className="flex gap-4">
              <label className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border-2 transition-all ${
                formData.registration_status === 'aktif' || formData.registration_status === 'aktif_sekarang'
                  ? 'bg-green-100 dark:bg-green-900/30 border-green-500 dark:border-green-500/50 text-green-700 dark:text-green-300'
                  : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:border-green-400 text-slate-700 dark:text-slate-300'
              }`}>
                <input
                  type="radio"
                  value="aktif"
                  checked={formData.registration_status === 'aktif' || formData.registration_status === 'aktif_sekarang'}
                  onChange={(e) => setFormData({...formData, registration_status: 'aktif'})}
                  className="w-4 h-4 text-green-600 dark:text-green-400"
                />
                <span className="font-medium">✓ Aktif Sekarang</span>
              </label>
              <label className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border-2 transition-all ${
                formData.registration_status === 'on_process'
                  ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-500 dark:border-amber-500/50 text-amber-700 dark:text-amber-300'
                  : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:border-amber-400 text-slate-700 dark:text-slate-300'
              }`}>
                <input
                  type="radio"
                  value="on_process"
                  checked={formData.registration_status === 'on_process'}
                  onChange={(e) => setFormData({...formData, registration_status: 'on_process'})}
                  className="w-4 h-4 text-amber-600 dark:text-amber-400"
                />
                <span className="font-medium">⏳ On Process</span>
              </label>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Tipe Pelanggan</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="reguler"
                  checked={formData.customer_type === 'reguler'}
                  onChange={(e) => setFormData({...formData, customer_type: e.target.value})}
                  className="w-4 h-4"
                />
                <span>Reguler</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="non_reguler"
                  checked={formData.customer_type === 'non_reguler'}
                  onChange={(e) => setFormData({...formData, customer_type: e.target.value})}
                  className="w-4 h-4"
                />
                <span>Non-Reguler</span>
              </label>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Tipe Pembayaran</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="prepaid"
                  checked={formData.payment_type === 'prepaid'}
                  onChange={(e) => setFormData({...formData, payment_type: e.target.value, payment_status: 'sudah_bayar'})}
                  className="w-4 h-4"
                />
                <span>Prepaid</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="postpaid"
                  checked={formData.payment_type === 'postpaid'}
                  onChange={(e) => setFormData({...formData, payment_type: e.target.value, payment_status: 'pending'})}
                  className="w-4 h-4"
                />
                <span>Postpaid</span>
              </label>
            </div>
          </div>

          {/* FIX 2026-10-05 (Delta #39): Tanggal Billing bisa dipilih saat create — sebelumnya tidak ada input,
              sehingga billing_date/due_date selalu default 1/10 dan tagihan "loncat" tanggalnya saat renewal */}
          <div className="space-y-2">
            <Label>Tanggal Billing (Jatuh Tempo)</Label>
            <select
              data-testid="customer-form-billing-date"
              className="w-full p-2.5 border-2 border-slate-300 rounded-lg bg-white cursor-pointer hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%278%27 viewBox=%270 0 12 8%27%3e%3cpath fill=%27%23334155%27 d=%27M6 8L0 0h12z%27/%3e%3c/svg%3e')] bg-[length:12px] bg-[center_right_0.75rem] bg-no-repeat pr-10 dark:border-slate-600 dark:bg-slate-800"
              value={formData.billing_date || _defaultBillingDay}
              onChange={(e) => setFormData({...formData, billing_date: parseInt(e.target.value, 10), due_date: parseInt(e.target.value, 10)})}
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>Tanggal {d}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 dark:text-slate-400">Tagihan terbit & jatuh tempo setiap tanggal ini tiap bulan (berlaku untuk prepaid & postpaid)</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Status Bayar *</Label>
              <select
                required
                className="w-full p-2.5 border-2 border-slate-300 rounded-lg bg-white cursor-pointer hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%278%27 viewBox=%270 0 12 8%27%3e%3cpath fill=%27%23334155%27 d=%27M6 8L0 0h12z%27/%3e%3c/svg%3e')] bg-[length:12px] bg-[center_right_0.75rem] bg-no-repeat pr-10 dark:border-slate-600 dark:bg-slate-800"
                value={formData.payment_status || 'sudah_bayar'}
                onChange={(e) => {
                  console.log('Payment status changed to:', e.target.value);
                  setFormData({...formData, payment_status: e.target.value});
                }}
              >
                <option value="sudah_bayar">Sudah Bayar</option>
                <option value="belum_bayar">Belum Bayar</option>
                <option value="pending">Pending</option>
              </select>
              <p className="text-xs text-slate-500 dark:text-slate-400">💡 Prepaid → Sudah Bayar | Postpaid → Pending</p>
            </div>
            <div className="space-y-2">
              <Label>Status Akun *</Label>
              <select
                required
                className="w-full p-2.5 border-2 border-slate-300 rounded-lg bg-white cursor-pointer hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%278%27 viewBox=%270 0 12 8%27%3e%3cpath fill=%27%23334155%27 d=%27M6 8L0 0h12z%27/%3e%3c/svg%3e')] bg-[length:12px] bg-[center_right_0.75rem] bg-no-repeat pr-10 dark:border-slate-600 dark:bg-slate-800"
                value={formData.account_status}
                onChange={(e) => setFormData({...formData, account_status: e.target.value})}
              >
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Biaya Bulanan (Rp) *</Label>
              <Input
                type="number"
                required
                placeholder="100000"
                value={formData.monthly_fee}
                onChange={(e) => setFormData({...formData, monthly_fee: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div className="space-y-2">
              <Label>Tanggal Pemasangan *</Label>
              <Input
                type="date"
                required
                value={formData.installation_date}
                onChange={(e) => setFormData({...formData, installation_date: e.target.value})}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="bind-on-login"
              checked={formData.bind_on_login}
              onChange={(e) => setFormData({...formData, bind_on_login: e.target.checked})}
              className="w-4 h-4"
            />
            <Label htmlFor="bind-on-login" className="cursor-pointer">Bind On Login</Label>
          </div>

        </TabsContent>

        {/* TAB 3: INFO BILLING */}
        <TabsContent value="info-billing" className="space-y-4 mt-4">
          {/* SECTION: CREDENTIALS - PPPoE/Hotspot OR Static IP config */}
          {formData.connection_type?.toLowerCase() === 'static' ? (
            <div className="p-5 bg-gradient-to-r from-blue-100 via-cyan-100 to-sky-100 dark:from-blue-900/40 dark:via-cyan-900/40 dark:to-sky-900/40 rounded-xl border-3 border-blue-400 dark:border-blue-600 shadow-lg shadow-blue-200/50 dark:shadow-blue-900/30">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-base font-bold text-blue-800 dark:text-blue-200">Konfigurasi IP Statik</h4>
                  <p className="text-xs text-blue-600 dark:text-blue-400">Alamat IP & MAC untuk pelanggan koneksi statik / DHCP binding</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-blue-800 dark:text-blue-200 font-semibold flex items-center gap-1" data-testid="label-static-ip">
                    <span className="text-red-500">*</span> IP Address Statik
                  </Label>
                  <Input
                    type="text"
                    placeholder="192.168.4.3"
                    data-testid="input-static-ip"
                    value={formData.static_ip || ''}
                    onChange={(e) => setFormData({...formData, static_ip: e.target.value})}
                    className="border-2 border-blue-400 focus:border-blue-600 focus:ring-blue-500 bg-white dark:bg-slate-800 font-mono text-blue-900 dark:text-blue-100"
                  />
                  <p className="text-[10px] text-blue-700/70 dark:text-blue-300/70">Format: 192.168.x.x — opsional /CIDR (mis. /24)</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-blue-800 dark:text-blue-200 font-semibold flex items-center gap-1" data-testid="label-static-mac">
                    MAC Address <span className="text-[10px] font-normal text-blue-600/70">(opsional)</span>
                  </Label>
                  <Input
                    type="text"
                    placeholder="AA:BB:CC:DD:EE:FF"
                    data-testid="input-static-mac"
                    value={formData.static_mac || ''}
                    onChange={(e) => setFormData({...formData, static_mac: e.target.value.toUpperCase()})}
                    className="border-2 border-blue-400 focus:border-blue-600 focus:ring-blue-500 bg-white dark:bg-slate-800 font-mono text-blue-900 dark:text-blue-100"
                  />
                  <p className="text-[10px] text-blue-700/70 dark:text-blue-300/70">Untuk DHCP static lease / ARP binding di MikroTik</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-blue-800 dark:text-blue-200 font-semibold flex items-center gap-1" data-testid="label-static-gateway">
                    Gateway <span className="text-[10px] font-normal text-blue-600/70">(opsional)</span>
                  </Label>
                  <Input
                    type="text"
                    placeholder="192.168.4.1"
                    data-testid="input-static-gateway"
                    value={formData.static_gateway || ''}
                    onChange={(e) => setFormData({...formData, static_gateway: e.target.value})}
                    className="border-2 border-blue-400 focus:border-blue-600 focus:ring-blue-500 bg-white dark:bg-slate-800 font-mono text-blue-900 dark:text-blue-100"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-blue-800 dark:text-blue-200 font-semibold flex items-center gap-1" data-testid="label-static-interface">
                    Interface MikroTik <span className="text-[10px] font-normal text-blue-600/70">(opsional)</span>
                  </Label>
                  <Input
                    type="text"
                    placeholder="ether1"
                    data-testid="input-static-interface"
                    value={formData.static_interface || ''}
                    onChange={(e) => setFormData({...formData, static_interface: e.target.value})}
                    className="border-2 border-blue-400 focus:border-blue-600 focus:ring-blue-500 bg-white dark:bg-slate-800 font-mono text-blue-900 dark:text-blue-100"
                  />
                </div>
              </div>
              <div className="mt-3 p-2.5 bg-amber-50 dark:bg-amber-900/30 border border-amber-300 rounded-lg">
                <p className="text-[11px] text-amber-800 dark:text-amber-200">
                  <span className="font-bold">ℹ️ Catatan:</span> Saat ini data IP/MAC hanya disimpan ke database.
                  Push otomatis ke MikroTik (DHCP lease + queue) akan ditambahkan pada update berikutnya.
                </p>
              </div>
            </div>
          ) : (
            <div className="p-5 bg-gradient-to-r from-green-100 via-emerald-100 to-teal-100 dark:from-green-900/40 dark:via-emerald-900/40 dark:to-teal-900/40 rounded-xl border-3 border-green-400 dark:border-green-600 shadow-lg shadow-green-200/50 dark:shadow-green-900/30">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 bg-green-500 rounded-lg">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-base font-bold text-green-800 dark:text-green-200">
                    {formData.connection_type?.toLowerCase() === 'hotspot' ? 'Kredensial Hotspot' : 'Kredensial PPPoE / RADIUS'}
                  </h4>
                  <p className="text-xs text-green-600 dark:text-green-400">Username & Password untuk koneksi internet</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-green-800 dark:text-green-200 font-semibold flex items-center gap-1">
                    <span className="text-red-500">*</span> Username {formData.connection_type?.toLowerCase() === 'hotspot' ? 'Hotspot' : 'PPPoE'}
                  </Label>
                  <Input
                    type="text"
                    placeholder={formData.connection_type?.toLowerCase() === 'hotspot' ? 'username_hotspot' : 'username_pppoe'}
                    value={formData.mikrotik_user || ''}
                    onChange={(e) => setFormData({...formData, mikrotik_user: e.target.value})}
                    data-testid="input-pppoe-username"
                    className={`border-2 ${formData._username_taken ? 'border-red-500 focus:border-red-600' : 'border-green-400 focus:border-green-600'} focus:ring-green-500 bg-white dark:bg-slate-800 font-medium text-green-900 dark:text-green-100`}
                  />
                  <UsernameStatus check={unameCheck} onPick={(s) => setFormData((f) => ({ ...f, mikrotik_user: s }))} />
                </div>
                <div className="space-y-2">
                  <Label className="text-green-800 dark:text-green-200 font-semibold flex items-center gap-1">
                    <span className="text-red-500">*</span> Password {formData.connection_type?.toLowerCase() === 'hotspot' ? 'Hotspot' : 'PPPoE'}
                  </Label>
                  <Input
                    type="text"
                    placeholder={formData.connection_type?.toLowerCase() === 'hotspot' ? 'password_hotspot' : 'password_pppoe'}
                    value={formData.mikrotik_password || ''}
                    onChange={(e) => setFormData({...formData, mikrotik_password: e.target.value})}
                    className="border-2 border-green-400 focus:border-green-600 focus:ring-green-500 bg-white dark:bg-slate-800 font-medium text-green-900 dark:text-green-100"
                  />
                </div>
              </div>
            </div>
          )}

          {/* SECTION: BILLING OPTIONS */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="bill-ppn"
                checked={formData.bill_ppn}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setFormData({
                    ...formData, 
                    bill_ppn: checked,
                    tax_percentage: checked ? (formData.tax_percentage || 11) : 0
                  });
                }}
                className="w-4 h-4"
              />
              <Label htmlFor="bill-ppn" className="cursor-pointer">Tagihkan PPN</Label>
            </div>

            {/* PPN Options - Only show when checkbox is checked */}
            {formData.bill_ppn && (
              <div className="ml-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 space-y-3">
                <Label className="text-sm font-semibold text-amber-900 dark:text-amber-100">Pilih Persentase PPN</Label>
                
                {/* Quick Select Buttons */}
                <div className="flex gap-2 flex-wrap">
                  {[11, 12].map((percent) => (
                    <button
                      key={percent}
                      type="button"
                      onClick={() => setFormData({...formData, tax_percentage: percent})}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        formData.tax_percentage === percent
                          ? 'bg-amber-600 text-white'
                          : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 hover:bg-amber-50 dark:hover:bg-amber-900/30'
                      }`}
                    >
                      {percent}%
                    </button>
                  ))}
                </div>

                {/* Manual Input */}
                <div className="space-y-2">
                  <Label className="text-xs text-slate-600 dark:text-slate-300">Atau Input Manual (%)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="Masukkan persentase PPN (koma/titik OK, mis. 11 atau 32,5)"
                    value={formData._tax_input ?? (formData.tax_percentage || '')}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // Allow only digits + one comma/dot
                      const cleaned = raw.replace(/[^0-9.,]/g, '').replace(/,/, '.');
                      const numeric = parseFloat(cleaned);
                      const value = isNaN(numeric) ? 0 : Math.min(100, Math.max(0, numeric));
                      setFormData({...formData, _tax_input: raw, tax_percentage: value});
                    }}
                    className="dark:bg-slate-700 dark:text-white"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">Contoh: 11 untuk PPN 11%</p>
                </div>

                {/* PPN Calculation Display */}
                {formData.monthly_fee > 0 && formData.tax_percentage > 0 && (
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-amber-300 dark:border-amber-700">
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Harga Paket:</span>
                        <span className="font-semibold">Rp {formData.monthly_fee.toLocaleString('id-ID')}</span>
                      </div>
                      <div className="flex justify-between text-amber-600 dark:text-amber-400">
                        <span>PPN ({formData.tax_percentage}%):</span>
                        <span className="font-semibold">+ Rp {((formData.monthly_fee * formData.tax_percentage) / 100).toLocaleString('id-ID', {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100">
                        <span className="font-bold">Total Tagihan (+ PPN):</span>
                        <span className="font-bold text-lg">
                          Rp {(formData.monthly_fee + (formData.monthly_fee * formData.tax_percentage / 100)).toLocaleString('id-ID', {maximumFractionDigits: 0})}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* PRORATE SECTION - ONLY FOR PPPoE, NOT HOTSPOT */}
            {formData.connection_type?.toLowerCase() !== 'hotspot' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="auto-prorate"
                  checked={formData.auto_prorate}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData({
                      ...formData, 
                      auto_prorate: checked,
                      prorate_date: checked ? null : null,
                      prorate_days_remaining: checked ? 0 : 0
                    });
                  }}
                  className="w-4 h-4"
                />
                <Label htmlFor="auto-prorate" className="cursor-pointer">Prorate Otomatis</Label>
              </div>

              {/* Prorate Options - Only show when checkbox is checked */}
              {formData.auto_prorate && (
                <div className="ml-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 space-y-3">
                  <Label className="text-sm font-semibold text-green-900 dark:text-green-100">Tanggal Pembayaran/Aktivasi</Label>
                  
                  {/* Quick Select Date Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    {[1, 5, 10].map((date) => (
                      <button
                        key={date}
                        type="button"
                        onClick={() => {
                          const daysRemaining = ((dueDay) => {
                            if (!dueDay || dueDay < 1) return 0;
                            const t = new Date();
                            const activation = t.getDate();
                            const lastDay = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
                            let d = activation <= dueDay ? (dueDay - activation) : ((lastDay - activation) + dueDay);
                            return Math.max(0, Math.min(30, d));
                          })(date);
                          setFormData({
                            ...formData, 
                            prorate_date: date,
                            prorate_days_remaining: daysRemaining
                          });
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          formData.prorate_date === date
                            ? 'bg-green-600 text-white'
                            : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 hover:bg-green-50 dark:hover:bg-green-900/30'
                        }`}
                      >
                        Tanggal {date}
                      </button>
                    ))}
                  </div>

                  {/* Manual Date Input */}
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-600 dark:text-slate-300">Atau Pilih Tanggal Manual (1-30)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="30"
                      placeholder="0 (Kosong = Tidak Prorate)"
                      value={formData.prorate_date || ''}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (inputValue === '' || inputValue === '0') {
                          // Clear prorate if empty or 0
                          setFormData({
                            ...formData, 
                            prorate_date: null,
                            prorate_days_remaining: 0
                          });
                        } else {
                          const date = Math.min(30, Math.max(1, parseInt(inputValue)));
                          const daysRemaining = ((dueDay) => {
                            if (!dueDay || dueDay < 1) return 0;
                            const t = new Date();
                            const activation = t.getDate();
                            const lastDay = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
                            let d = activation <= dueDay ? (dueDay - activation) : ((lastDay - activation) + dueDay);
                            return Math.max(0, Math.min(30, d));
                          })(date);
                          setFormData({
                            ...formData, 
                            prorate_date: date,
                            prorate_days_remaining: daysRemaining
                          });
                        }
                      }}
                      className="dark:bg-slate-700 dark:text-white"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400">💡 Kosongkan atau isi 0 jika tidak mau prorate. Contoh: 15 (aktivasi tanggal 15)</p>
                  </div>

                  {/* Prorate Calculation Display */}
                  {formData.monthly_fee > 0 && formData.prorate_date && (
                    <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-green-300 dark:border-green-700">
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600 dark:text-slate-400">Tanggal Aktivasi:</span>
                          <span className="font-semibold">Tanggal {formData.prorate_date}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600 dark:text-slate-400">Sisa Hari Bulan Ini:</span>
                          <span className="font-semibold">{formData.prorate_days_remaining} hari</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600 dark:text-slate-400">Harga Paket (30 hari):</span>
                          <span className="font-semibold">Rp {formData.monthly_fee.toLocaleString('id-ID')}</span>
                        </div>
                        <div className="flex justify-between text-blue-600 dark:text-blue-400">
                          <span>Harga per Hari:</span>
                          <span className="font-semibold">Rp {(formData.monthly_fee / 30).toLocaleString('id-ID', {maximumFractionDigits: 0})}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-green-200 dark:border-green-800 text-green-900 dark:text-green-100">
                          <span className="font-bold">Biaya Prorate:</span>
                          <span className="font-bold text-lg">
                            Rp {((formData.monthly_fee / 30) * formData.prorate_days_remaining).toLocaleString('id-ID', {maximumFractionDigits: 0})}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 pt-2">
                          * Bulan berikutnya akan ditagih penuh Rp {formData.monthly_fee.toLocaleString('id-ID')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="activate-promo"
                  checked={formData.activate_promo}
                  onChange={(e) => setFormData({...formData, activate_promo: e.target.checked, promo_percentage: e.target.checked ? formData.promo_percentage : 0})}
                  className="w-4 h-4"
                />
                <Label htmlFor="activate-promo" className="cursor-pointer">Aktifkan Promo</Label>
              </div>

              {/* Promo Options - Only show when checkbox is checked */}
              {formData.activate_promo && (
                <div className="ml-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800 space-y-3">
                  <Label className="text-sm font-semibold text-indigo-900 dark:text-indigo-100">Pilih Persentase Diskon</Label>
                  
                  {/* Quick Select Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    {[10, 20, 30, 40, 100].map((percent) => (
                      <button
                        key={percent}
                        type="button"
                        onClick={() => setFormData({...formData, promo_percentage: percent})}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          formData.promo_percentage === percent
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30'
                        }`}
                      >
                        {percent}%
                      </button>
                    ))}
                  </div>

                  {/* Manual Input */}
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-600 dark:text-slate-300">Atau Input Manual (%)</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="Masukkan persentase (koma/titik OK, mis. 32,5)"
                      value={formData._promo_input ?? (formData.promo_percentage || '')}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const cleaned = raw.replace(/[^0-9.,]/g, '').replace(/,/, '.');
                        const numeric = parseFloat(cleaned);
                        const value = isNaN(numeric) ? 0 : Math.min(100, Math.max(0, numeric));
                        setFormData({...formData, _promo_input: raw, promo_percentage: value});
                      }}
                      className="dark:bg-slate-700 dark:text-white"
                    />
                  </div>

                  {/* Durasi Diskon */}
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-indigo-900 dark:text-indigo-100">📅 Durasi Diskon (Bulan)</Label>
                    
                    {/* Quick Select Buttons for Duration */}
                    <div className="flex gap-2 flex-wrap">
                      {[
                        { value: 0, label: 'Unlimited', info: 'Tanpa Batas' },
                        { value: 1, label: '1 Bulan', info: 'Hanya bulan pertama' },
                        { value: 3, label: '3 Bulan', info: '3 bulan pertama' },
                        { value: 6, label: '6 Bulan', info: '6 bulan pertama' },
                        { value: 12, label: '12 Bulan', info: '1 tahun pertama' }
                      ].map((duration) => (
                        <button
                          key={duration.value}
                          type="button"
                          onClick={() => setFormData({...formData, promo_duration_months: duration.value})}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            formData.promo_duration_months === duration.value
                              ? 'bg-green-600 text-white'
                              : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 hover:bg-green-50 dark:hover:bg-green-900/30'
                          }`}
                          title={duration.info}
                        >
                          {duration.label}
                        </button>
                      ))}
                    </div>

                    {/* Manual Input */}
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-600 dark:text-slate-300">Atau Input Manual (Bulan)</Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="0 = Tanpa Batas"
                        value={formData.promo_duration_months || ''}
                        onChange={(e) => {
                          const value = Math.max(0, parseInt(e.target.value) || 0);
                          setFormData({...formData, promo_duration_months: value});
                        }}
                        className="dark:bg-slate-700 dark:text-white"
                      />
                    </div>

                    {/* Info Box */}
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                      <p className="text-xs text-green-800 dark:text-green-200">
                        {formData.promo_duration_months === 0 || !formData.promo_duration_months ? (
                          <>
                            <strong>🔄 Unlimited:</strong> Diskon berlaku <strong>SELAMANYA</strong> (tanpa batas waktu)
                          </>
                        ) : formData.promo_duration_months === 1 ? (
                          <>
                            <strong>📅 1 Bulan:</strong> Diskon berlaku <strong>hanya 1 bulan pertama</strong>, lalu harga kembali normal
                          </>
                        ) : formData.promo_duration_months === 3 ? (
                          <>
                            <strong>📅 3 Bulan:</strong> Diskon berlaku <strong>3 bulan pertama</strong>, lalu harga kembali normal
                          </>
                        ) : formData.promo_duration_months === 6 ? (
                          <>
                            <strong>📅 6 Bulan:</strong> Diskon berlaku <strong>6 bulan pertama</strong>, lalu harga kembali normal
                          </>
                        ) : formData.promo_duration_months === 12 ? (
                          <>
                            <strong>📅 12 Bulan:</strong> Diskon berlaku <strong>12 bulan pertama</strong> (1 tahun), lalu harga kembali normal
                          </>
                        ) : (
                          <>
                            <strong>📅 {formData.promo_duration_months} Bulan:</strong> Diskon berlaku <strong>{formData.promo_duration_months} bulan pertama</strong>, lalu harga kembali normal
                          </>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Calculation Display */}
                  {formData.monthly_fee > 0 && formData.promo_percentage > 0 && (
                    <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-indigo-300 dark:border-indigo-700">
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600 dark:text-slate-400">Harga Paket:</span>
                          <span className="font-semibold">Rp {formData.monthly_fee.toLocaleString('id-ID')}</span>
                        </div>
                        <div className="flex justify-between text-red-600 dark:text-red-400">
                          <span>Diskon ({formData.promo_percentage}%):</span>
                          <span className="font-semibold">- Rp {((formData.monthly_fee * formData.promo_percentage) / 100).toLocaleString('id-ID')}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-indigo-200 dark:border-indigo-800 text-indigo-900 dark:text-indigo-100">
                          <span className="font-bold">Harga Setelah Diskon:</span>
                          <span className="font-bold text-lg">Rp {(formData.monthly_fee - (formData.monthly_fee * formData.promo_percentage / 100)).toLocaleString('id-ID')}</span>
                        </div>
                        {formData.promo_duration_months > 0 && (
                          <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                            <p className="text-xs text-amber-600 dark:text-amber-400">
                              ⏱️ Diskon berlaku untuk <strong>{formData.promo_duration_months} invoice pertama</strong>. Setelah itu harga kembali normal.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Biaya Instalasi</Label>
              <Input
                type="number"
                placeholder="0"
                value={formData.installation_fee}
                onChange={(e) => setFormData({...formData, installation_fee: parseFloat(e.target.value) || 0})}
              />
              <p className="text-[10px] text-slate-500 dark:text-slate-400">Sekali (invoice pertama)</p>
            </div>
            <div className="space-y-2">
              <Label>Fee Seller</Label>
              <Input
                type="number"
                placeholder="0"
                value={formData.seller_fee}
                onChange={(e) => setFormData({...formData, seller_fee: parseFloat(e.target.value) || 0})}
              />
              <p className="text-[10px] text-slate-500 dark:text-slate-400">&nbsp;</p>
            </div>
            <div className="space-y-2">
              <Label>Sewa Perangkat</Label>
              <Input
                type="number"
                placeholder="0"
                value={formData.device_rental}
                onChange={(e) => setFormData({...formData, device_rental: parseFloat(e.target.value) || 0})}
              />
              <p className="text-[10px] text-slate-500 dark:text-slate-400">Setiap bulan</p>
            </div>
            <div className="space-y-2">
              <Label>Biaya Lain-lain</Label>
              <Input
                type="number"
                placeholder="0"
                value={formData.other_fees}
                onChange={(e) => setFormData({...formData, other_fees: parseFloat(e.target.value) || 0})}
                data-testid="input-biaya-lain"
              />
              <p className="text-[10px] text-slate-500 dark:text-slate-400">Setiap bulan</p>
            </div>
          </div>

          {/* Final Price Summary - Only show if monthly_fee exists */}
          {formData.monthly_fee > 0 && (formData.bill_ppn || formData.auto_prorate || formData.activate_promo) && (
            <div className="border-2 border-blue-300 dark:border-blue-700 rounded-lg p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 mt-6">
              <h4 className="font-bold text-lg text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                <span className="text-2xl">💰</span>
                Ringkasan Harga Final untuk Invoice
              </h4>
              
              <div className="space-y-2 bg-white dark:bg-slate-800 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                {/* Base Price */}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400">Harga Paket Standar:</span>
                  <span className="font-semibold">Rp {formData.monthly_fee.toLocaleString('id-ID')}</span>
                </div>

                {/* Promo Discount */}
                {formData.activate_promo && formData.promo_percentage > 0 && (
                  <div className="flex justify-between text-sm text-red-600 dark:text-red-400">
                    <span>Diskon Promo ({formData.promo_percentage}%):</span>
                    <span className="font-semibold">- Rp {((formData.monthly_fee * formData.promo_percentage) / 100).toLocaleString('id-ID', {maximumFractionDigits: 0})}</span>
                  </div>
                )}

                {/* Price after promo */}
                {formData.activate_promo && formData.promo_percentage > 0 && (
                  <div className="flex justify-between text-sm border-t border-slate-200 dark:border-slate-700 pt-2">
                    <span className="text-slate-600 dark:text-slate-400">Harga Setelah Promo:</span>
                    <span className="font-semibold">Rp {(formData.monthly_fee - (formData.monthly_fee * formData.promo_percentage / 100)).toLocaleString('id-ID', {maximumFractionDigits: 0})}</span>
                  </div>
                )}

                {/* Calculate base for PPN and Prorate */}
                {(() => {
                  const basePrice = formData.activate_promo && formData.promo_percentage > 0 
                    ? formData.monthly_fee - (formData.monthly_fee * formData.promo_percentage / 100)
                    : formData.monthly_fee;

                  const proratePrice = formData.auto_prorate && formData.prorate_days_remaining > 0
                    ? (basePrice / 30) * formData.prorate_days_remaining
                    : basePrice;

                  const ppnAmount = formData.bill_ppn && formData.tax_percentage > 0
                    ? (proratePrice * formData.tax_percentage) / 100
                    : 0;

                  const finalPrice = proratePrice + ppnAmount;

                  // Update formData with final_price
                  if (formData.final_invoice_price !== finalPrice) {
                    setTimeout(() => {
                      setFormData({...formData, final_invoice_price: finalPrice});
                    }, 0);
                  }

                  return (
                    <>
                      {/* Prorate Adjustment */}
                      {formData.auto_prorate && formData.prorate_days_remaining > 0 && (
                        <>
                          <div className="flex justify-between text-sm text-blue-600 dark:text-blue-400">
                            <span>Prorate ({formData.prorate_days_remaining} hari dari 30 hari):</span>
                            <span className="font-semibold">Rp {proratePrice.toLocaleString('id-ID', {maximumFractionDigits: 0})}</span>
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400 ml-4">
                            (Rp {(basePrice / 30).toLocaleString('id-ID', {maximumFractionDigits: 0})} × {formData.prorate_days_remaining} hari)
                          </div>
                        </>
                      )}

                      {/* PPN */}
                      {formData.bill_ppn && formData.tax_percentage > 0 && (
                        <div className="flex justify-between text-sm text-amber-600 dark:text-amber-400 border-t border-slate-200 dark:border-slate-700 pt-2">
                          <span>PPN ({formData.tax_percentage}%):</span>
                          <span className="font-semibold">+ Rp {ppnAmount.toLocaleString('id-ID', {maximumFractionDigits: 0})}</span>
                        </div>
                      )}

                      {/* Final Price */}
                      <div className="flex justify-between border-t-2 border-blue-400 dark:border-blue-600 pt-3 mt-2">
                        <div>
                          <div className="font-bold text-base text-blue-900 dark:text-blue-100">HARGA INVOICE:</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            (Harga ini yang akan ditagih di invoice)
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-2xl text-blue-900 dark:text-blue-100">
                            Rp {finalPrice.toLocaleString('id-ID', {maximumFractionDigits: 0})}
                          </div>
                        </div>
                      </div>

                      {/* Note */}
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-2 mt-3">
                        <p className="text-xs text-yellow-800 dark:text-yellow-200">
                          <strong>📌 Catatan:</strong> Harga paket standar <strike>Rp {formData.monthly_fee.toLocaleString('id-ID')}</strike> diabaikan. 
                          Sistem akan menggunakan harga <strong>Rp {finalPrice.toLocaleString('id-ID', {maximumFractionDigits: 0})}</strong> untuk invoice bulan ini.
                          {formData.auto_prorate && formData.prorate_days_remaining > 0 && (
                            <span> Bulan berikutnya akan ditagih harga {formData.activate_promo && formData.promo_percentage > 0 ? 'setelah promo' : 'standar'}.</span>
                          )}
                        </p>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </TabsContent>
        <TabsContent value="perangkat" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <Label>Merk Perangkat</Label>
              <Input
                list="device-brand-options"
                data-testid="device-brand-trigger"
                value={formData.device_brand || ''}
                onChange={(e) => setFormData({ ...formData, device_brand: e.target.value })}
                placeholder="Pilih dari daftar atau ketik merk baru"
                maxLength={30}
                autoComplete="off"
              />
              <datalist id="device-brand-options">
                <option value="Huawei" />
                <option value="ZTE" />
                <option value="Fiberhome" />
                <option value="HSGQ" />
                <option value="VSOL" />
                <option value="GGCLINK" />
                <option value="CDATA" />
                <option value="Raisecom" />
                <option value="Global" />
                <option value="GM220S" />
                <option value="MQ220" />
                <option value="TOTOLINK" />
                <option value="TPLINK" />
                <option value="ZIMMLINK" />
                <option value="TENDA" />
                <option value="MYLINK" />
              </datalist>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Tap/klik untuk melihat daftar merk, atau ketik merk lain kalau tidak ada di daftar.
              </p>
            </div>

            <div>
              <Label>Model / Tipe</Label>
              <Input
                data-testid="device-model-input"
                value={formData.device_model || ''}
                onChange={(e) => setFormData({ ...formData, device_model: e.target.value })}
                placeholder="mis. HG8546M, F660 v6, dsb."
                maxLength={50}
              />
            </div>

            <div>
              <Label>Serial Number (SN)</Label>
              <Input
                data-testid="device-sn-input"
                value={formData.device_sn || ''}
                onChange={(e) => setFormData({ ...formData, device_sn: e.target.value.trim() })}
                placeholder="SN unik perangkat"
                maxLength={80}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                SN harus unik per tenant. Kosongkan jika belum diketahui.
              </p>
            </div>

            <div>
              <Label>Band / Frekuensi</Label>
              <Select
                value={formData.device_band || ''}
                onValueChange={(v) => setFormData({ ...formData, device_band: v })}
              >
                <SelectTrigger data-testid="device-band-trigger">
                  <SelectValue placeholder="— Pilih band —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2.4Ghz">2.4 GHz</SelectItem>
                  <SelectItem value="5.8Ghz">5.8 GHz</SelectItem>
                  <SelectItem value="Dual Band">Dual Band (2.4 + 5)</SelectItem>
                  <SelectItem value="WIFI6">WiFi 6</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-4">
            <Label>Foto Perangkat</Label>
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4 mt-2">
              {formData.device_photo_url ? (
                <div className="relative shrink-0">
                  <img
                    src={
                      formData.device_photo_url.startsWith('http')
                        ? formData.device_photo_url
                        : `${API_URL}${formData.device_photo_url}`
                    }
                    alt="Foto perangkat pelanggan"
                    className="w-28 h-28 sm:w-32 sm:h-32 object-cover rounded border border-slate-300 dark:border-slate-700 shrink-0"
                    data-testid="device-photo-preview"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, device_photo_url: '' })}
                    className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs shadow"
                    title="Hapus foto"
                    data-testid="device-photo-remove-btn"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="w-28 h-28 sm:w-32 sm:h-32 flex items-center justify-center rounded border-2 border-dashed border-slate-300 dark:border-slate-700 text-slate-400 text-xs text-center px-2 shrink-0">
                  Belum ada foto
                </div>
              )}

              <div className="flex-1 w-full min-w-0">
                {/* Hidden inputs — one for camera, one for gallery */}
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  data-testid="device-photo-input"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    await handleDevicePhotoUpload(file);
                    e.target.value = '';
                  }}
                />
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  className="hidden"
                  data-testid="device-photo-gallery-input"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    await handleDevicePhotoUpload(file);
                    e.target.value = '';
                  }}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    type="button"
                    disabled={devicePhotoUploading}
                    onClick={() => cameraInputRef.current?.click()}
                    data-testid="device-photo-camera-btn"
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-200 font-medium text-sm hover:bg-indigo-100 dark:hover:bg-indigo-900/50 active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {devicePhotoUploading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Camera className="w-5 h-5" />
                    )}
                    <span>{devicePhotoUploading ? 'Mengunggah...' : 'Ambil Foto'}</span>
                  </button>
                  <button
                    type="button"
                    disabled={devicePhotoUploading}
                    onClick={() => galleryInputRef.current?.click()}
                    data-testid="device-photo-gallery-btn"
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-medium text-sm hover:bg-slate-50 dark:hover:bg-slate-700 active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ImageIcon className="w-5 h-5" />
                    <span>Pilih dari Galeri</span>
                  </button>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                  Di HP: tombol "Ambil Foto" akan langsung membuka kamera belakang. Otomatis di-compress ke max 1280px.
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default CustomerFormTabs;
